/**
 * Production Charts Component - Display trend charts for production data
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  BarChart3,
  Zap,
  Activity
} from 'lucide-react';
import { useProductionData } from '@/hooks/useProductionData';
import { ProductionFilters } from '@/types/production';

interface ProductionChartsProps {
  filters: ProductionFilters;
}

export const ProductionCharts: React.FC<ProductionChartsProps> = ({ filters }) => {
  const [activeChart, setActiveChart] = useState<'labor' | 'volume' | 'productivity'>('productivity');

  // Fetch production data
  const {
    data: rawData = [],
    isLoading,
    error
  } = useProductionData(filters);

  // Transform data for chart display
  const chartData = React.useMemo(() => {
    if (!rawData.length) {
      console.log('📊 No raw data available for charts');
      return [];
    }

    console.log('📊 Raw production data for charts:', rawData.length, 'records');
    console.log('📊 Sample raw data:', rawData[0]);
    console.log('📊 Active chart type:', activeChart);

    return rawData.map(item => {
      const date = new Date(item.DocDate).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit'
      });

      // Prepare data based on chart type with safe value extraction
      const baseData = { date, fullDate: item.DocDate };

      // Helper function to safely extract numeric values
      const safeNumber = (value: any): number => {
        const num = Number(value);
        return isNaN(num) || num < 0 ? 0 : num;
      };

      if (activeChart === 'labor') {
        const result = {
          ...baseData,
          'Sơ chế': safeNumber(item.CSChe),
          'Tinh chế': safeNumber(item.CTChe), 
          'Lắp ráp': safeNumber(item.CLRap),
          'Nguội': safeNumber(item.CNguoi),
          'Sơn': safeNumber(item.CPSon),
        };
        console.log('📊 Labor data for', date, ':', result);
        return result;
      } else if (activeChart === 'volume') {
        const result = {
          ...baseData,
          'Sơ chế': safeNumber(item.M3SChe),
          'Tinh chế': safeNumber(item.M3TChe),
          'Lắp ráp': safeNumber(item.M3LRap),
          'Nguội': safeNumber(item.M3Nguoi),
          'Sơn': safeNumber(item.M3PSon),
        };
        console.log('📊 Volume data for', date, ':', result);
        return result;
      } else { // productivity
        const result = {
          ...baseData,
          'Sơ chế': safeNumber(item.NSChe),
          'Tinh chế': safeNumber(item.NSTChe),
          'Lắp ráp': safeNumber(item.NSLRap),
          'Nguội': safeNumber(item.NSNguoi),
          'Sơn': safeNumber(item.NSPSon),
        };
        console.log('📊 Productivity data for', date, ':', result);
        return result;
      }
    }).sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
  }, [rawData, activeChart]);

  // Debug: Log chart data after transformation
  React.useEffect(() => {
    if (chartData.length > 0) {
      console.log('📈 Transformed chart data:', chartData.length, 'records');
      console.log('📈 Sample chart data:', chartData[0]);
      console.log('📈 All process values in first record:', {
        'Sơ chế': chartData[0]['Sơ chế'],
        'Tinh chế': chartData[0]['Tinh chế'],
        'Lắp ráp': chartData[0]['Lắp ráp'],
        'Nguội': chartData[0]['Nguội'],
        'Sơn': chartData[0]['Sơn'],
      });
    }
  }, [chartData]);

  // Chart colors for each process (removed packaging as it has separate tab)
  const processColors = {
    'Sơ chế': '#8884d8',
    'Tinh chế': '#82ca9d',
    'Lắp ráp': '#ffc658',
    'Nguội': '#ff7300',
    'Sơn': '#00ff00',
  };

  // Calculate average values for trendlines based on working days logic
  const averageValues = React.useMemo(() => {
    if (!chartData.length) return {};
    
    const processes = Object.keys(processColors);
    const averages: Record<string, number> = {};
    
        // Find working days based on any production activity (any process has value > 0, excluding packaging)
        const workingDaysData = rawData.filter(item => 
      (item.CSChe > 0) || (item.CTChe > 0) || (item.CLRap > 0) || (item.CNguoi > 0) ||
      (item.CPSon > 0) ||
      (item.M3SChe > 0) || (item.M3TChe > 0) || (item.M3LRap > 0) || (item.M3Nguoi > 0) ||
      (item.M3PSon > 0) ||
      (item.NSChe > 0) || (item.NSTChe > 0) || (item.NSLRap > 0) || (item.NSNguoi > 0) ||
      (item.NSPSon > 0)
    );
    
    if (workingDaysData.length === 0) return {};
    
    processes.forEach(process => {
      const workingDaysValues = workingDaysData.map(item => {
        // Helper function to safely extract numeric values
        const safeNumber = (value: any): number => {
          const num = Number(value);
          return isNaN(num) || num < 0 ? 0 : num;
        };

        // Map working days data to chart values based on active chart type
        if (activeChart === 'labor') {
          const mapping: Record<string, any> = {
            'Sơ chế': item.CSChe,
            'Tinh chế': item.CTChe, 
            'Lắp ráp': item.CLRap,
            'Nguội': item.CNguoi,
            'Sơn': item.CPSon,
          };
          return safeNumber(mapping[process]);
        } else if (activeChart === 'volume') {
          const mapping: Record<string, any> = {
            'Sơ chế': item.M3SChe,
            'Tinh chế': item.M3TChe,
            'Lắp ráp': item.M3LRap,
            'Nguội': item.M3Nguoi,
            'Sơn': item.M3PSon,
          };
          return safeNumber(mapping[process]);
        } else { // productivity
          const mapping: Record<string, any> = {
            'Sơ chế': item.NSChe,
            'Tinh chế': item.NSTChe,
            'Lắp ráp': item.NSLRap,
            'Nguội': item.NSNguoi,
            'Sơn': item.NSPSon,
          };
          return safeNumber(mapping[process]);
        }
      });
      
      const sum = workingDaysValues.reduce((acc, val) => acc + val, 0);
      averages[process] = sum / workingDaysValues.length;
    });
    
    console.log('📊 Production chart averages:', {
      activeChart,
      totalDays: chartData.length,
      workingDays: workingDaysData.length,
      averages
    });
    
    return averages;
  }, [chartData, rawData, activeChart]);

  // Chart metadata
  const chartMeta = {
    labor: {
      title: 'Biểu đồ Công lao động',
      description: 'Theo dõi công lao động theo thời gian',
      unit: 'công',
      icon: Users,
      color: 'text-blue-600',
    },
    volume: {
      title: 'Biểu đồ Công suất',
      description: 'Theo dõi công suất sản xuất theo thời gian',
      unit: 'm³',
      icon: BarChart3,
      color: 'text-green-600',
    },
    productivity: {
      title: 'Biểu đồ Năng suất',
      description: 'Theo dõi năng suất sản xuất theo thời gian',
      unit: 'm³/công',
      icon: Zap,
      color: 'text-purple-600',
    },
  };

  const currentMeta = chartMeta[activeChart];
  const Icon = currentMeta.icon;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const fullDate = payload[0]?.payload?.fullDate;
      const displayDate = fullDate ? new Date(fullDate).toLocaleDateString('vi-VN') : label;
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{displayDate}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              <span className="font-medium">{entry.dataKey}:</span>{' '}
              <span>{Number(entry.value).toFixed(activeChart === 'productivity' ? 2 : 1)} {currentMeta.unit}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Lỗi tải biểu đồ</CardTitle>
          <CardDescription>{error.message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${currentMeta.color}`} />
            <div>
              <CardTitle>{currentMeta.title}</CardTitle>
              <CardDescription>{currentMeta.description}</CardDescription>
            </div>
          </div>
          
          {/* Chart Type Selector - Responsive */}
          <div className="flex flex-wrap gap-2 sm:flex-nowrap">
            <Button
              variant={activeChart === 'labor' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart('labor')}
              className="flex items-center gap-1 flex-1 sm:flex-none"
            >
              <Users className="h-3 w-3" />
              <span className="hidden xs:inline">Công lao động</span>
              <span className="xs:hidden">Công</span>
            </Button>
            <Button
              variant={activeChart === 'volume' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart('volume')}
              className={`flex items-center gap-1 flex-1 sm:flex-none ${activeChart === 'volume' ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
            >
              <BarChart3 className="h-3 w-3" />
              <span className="hidden xs:inline">M³ Công suất</span>
              <span className="xs:hidden">M³</span>
            </Button>
            <Button
              variant={activeChart === 'productivity' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart('productivity')}
              className="flex items-center gap-1 flex-1 sm:flex-none"
            >
              <Zap className="h-3 w-3" />
              <span className="hidden xs:inline">Năng suất</span>
              <span className="xs:hidden">NS</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Activity className="h-4 w-4 animate-spin" />
              Đang tải dữ liệu biểu đồ...
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 10,
                  left: 10,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }}
                  stroke="#666"
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  stroke="#666"
                  domain={['dataMin - 1', 'dataMax + 1']}
                  allowDataOverflow={false}
                  width={45}
                  tickFormatter={(value) => {
                    if (activeChart === 'productivity') {
                      return value.toFixed(1);
                    }
                    return value.toFixed(0);
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '12px' }}
                  iconSize={12}
                />
                
                {/* Trendlines - Average Reference Lines */}
                {Object.entries(averageValues).map(([process, avgValue]) => (
                  <ReferenceLine
                    key={`avg-${process}`}
                    y={avgValue}
                    stroke={processColors[process as keyof typeof processColors]}
                    strokeDasharray="6 3"
                    strokeWidth={1.5}
                    strokeOpacity={0.6}
                    label={{
                      value: `TB: ${avgValue.toFixed(activeChart === 'productivity' ? 1 : 0)}`,
                      position: 'insideTopRight',
                      fontSize: 9,
                      fill: processColors[process as keyof typeof processColors],
                      fontWeight: 'bold'
                    }}
                  />
                ))}
                
                {Object.keys(processColors).map((process) => {
                  const hasData = chartData.some(item => item[process] > 0);
                  console.log(`📈 Process "${process}" has data:`, hasData);
                  
                  return (
                    <Line
                      key={process}
                      type="monotone"
                      dataKey={process}
                      stroke={processColors[process as keyof typeof processColors]}
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 1.5, fill: processColors[process as keyof typeof processColors] }}
                      activeDot={{ r: 4, strokeWidth: 2, fill: processColors[process as keyof typeof processColors] }}
                      connectNulls={false}
                      name={process}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Không có dữ liệu để hiển thị biểu đồ</p>
              <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra kết nối mạng</p>
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
};